import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock, ShieldAlert } from 'lucide-react';

function computeProgressDenominatorMs(dialog, now = Date.now()) {
  const decisionSeconds = dialog.decisionSeconds ?? 30;
  const receivedAtMs = dialog.receivedAtMs;
  const localSpanMs = Math.max(1, decisionSeconds * 1000);

  if (!dialog.expiresAt) {
    return localSpanMs;
  }
  const serverDeadline = new Date(dialog.expiresAt).getTime();
  if (Number.isNaN(serverDeadline)) {
    return localSpanMs;
  }
  if (serverDeadline > now && serverDeadline > receivedAtMs) {
    return Math.max(1, serverDeadline - receivedAtMs);
  }
  return localSpanMs;
}

function computeProgressPercent(dialog, now = Date.now()) {
  const remainingMs = computeRemainingMs(dialog, now);
  const denomMs = computeProgressDenominatorMs(dialog, now);
  return Math.min(100, Math.max(0, (remainingMs / denomMs) * 100));
}

function computeRemainingMs(dialog, now = Date.now()) {
  const decisionSeconds = dialog.decisionSeconds ?? 30;
  const receivedAtMs = dialog.receivedAtMs;
  const localEndMs = receivedAtMs + decisionSeconds * 1000;

  if (!dialog.expiresAt) {
    return Math.max(0, localEndMs - now);
  }
  const serverDeadline = new Date(dialog.expiresAt).getTime();
  if (Number.isNaN(serverDeadline)) {
    return Math.max(0, localEndMs - now);
  }
  if (serverDeadline <= now) {
    return Math.max(0, localEndMs - now);
  }
  return Math.max(0, serverDeadline - now);
}

/**
 * Shown on the sign-in page (User B) when an active session exists elsewhere.
 */
export default function ActiveSessionLoginPrompt({ dialog }) {
  const progressFillRef = useRef(null);

  useEffect(() => {
    if (!dialog) {
      return undefined;
    }
    let cancelled = false;
    let expiryHandled = false;
    let rafId = 0;
    const step = () => {
      if (cancelled) return;
      const now = Date.now();
      const remainingMs = computeRemainingMs(dialog, now);
      const pct = computeProgressPercent(dialog, now);
      const el = progressFillRef.current;
      if (el) {
        el.style.width = `${pct}%`;
      }
      if (remainingMs <= 0) {
        if (!expiryHandled) {
          expiryHandled = true;
          dialog.resolve?.('cancel');
        }
        return;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [dialog]);

  useEffect(() => {
    if (!dialog) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dialog]);

  if (!dialog) {
    return null;
  }

  const initialProgressPct = computeProgressPercent(dialog);

  const modal = (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="active-session-login-title"
      aria-describedby="active-session-login-desc"
    >
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl border-2 border-amber-400 bg-white p-6 shadow-2xl ring-4 ring-amber-300/40 dark:border-amber-500 dark:bg-slate-900 dark:ring-amber-500/30 animate-in zoom-in-95 fade-in duration-200">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <ShieldAlert className="h-6 w-6" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="active-session-login-title"
              className="text-lg font-semibold text-slate-900 dark:text-white"
            >
              Active session in use
            </h2>
            <p
              id="active-session-login-desc"
              className="mt-1 text-sm text-slate-600 dark:text-slate-300"
            >
              You are already signed in on another device or browser. You can end that session and continue here, or
              cancel this sign-in.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-4 dark:border-amber-800 dark:bg-amber-950/40">
          <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
            <Clock className="h-5 w-5 shrink-0" aria-hidden />
            <span className="text-sm font-medium">Time remaining to decide</span>
          </div>
          <div
            className="mt-3 h-3 w-full overflow-hidden rounded-full bg-amber-200/90 dark:bg-amber-900/60"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(initialProgressPct)}
            aria-live="off"
            aria-label="Time remaining in the decision window"
          >
            <div
              ref={progressFillRef}
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-400 dark:to-amber-500"
              style={{ width: `${initialProgressPct}%` }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            onClick={() => dialog.resolve?.('cancel')}
          >
            Cancel login
          </button>
          <button
            type="button"
            className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-emerald-700"
            onClick={() => dialog.resolve?.('continue')}
            title="End the other session and complete sign-in on this device"
          >
            Close previous session and continue
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
